import { defaultClientAuthToken, isSameOriginAuthToken } from "./core/client-auth.js";

const API_KEY_STORAGE = "lytebulb.runtime.apiKey";
const ACCOUNT_STORAGE = "lytebulb.runtime.accountId";

export function loadAPIKey() {
  return window.localStorage.getItem(API_KEY_STORAGE) || defaultClientAuthToken();
}

export function saveAPIKey(value) {
  const trimmed = String(value || "").trim();
  if (!trimmed) {
    window.localStorage.removeItem(API_KEY_STORAGE);
    return defaultClientAuthToken();
  }
  window.localStorage.setItem(API_KEY_STORAGE, trimmed);
  return trimmed;
}

export function loadSelectedAccountID() {
  if (!globalThis.window?.localStorage) {
    return "";
  }
  return window.localStorage.getItem(ACCOUNT_STORAGE) || "";
}

export function saveSelectedAccountID(value) {
  const trimmed = String(value || "").trim();
  if (!globalThis.window?.localStorage) {
    return trimmed;
  }
  if (!trimmed) {
    window.localStorage.removeItem(ACCOUNT_STORAGE);
    return "";
  }
  window.localStorage.setItem(ACCOUNT_STORAGE, trimmed);
  return trimmed;
}

export async function apiGetJSON(apiKey, path, options = {}) {
  const response = await apiRequest(apiKey, path, { method: "GET", signal: options.signal });
  return response.json();
}

export async function apiPostJSON(apiKey, path, body, options = {}) {
  const response = await apiRequest(apiKey, path, {
    method: "POST",
    headers: withHeaders(options.headers, { "Content-Type": "application/json" }),
    body: JSON.stringify(body || {}),
    signal: options.signal
  });
  return response.json();
}

export async function apiPostJSONDetailed(apiKey, path, body, options = {}) {
  const response = await apiRequest(apiKey, path, {
    method: "POST",
    headers: withHeaders(options.headers, { "Content-Type": "application/json" }),
    body: JSON.stringify(body || {}),
    signal: options.signal
  });
  return parseAPIResponse(response);
}

export async function apiPatchJSON(apiKey, path, body, options = {}) {
  const response = await apiRequest(apiKey, path, {
    method: "PATCH",
    headers: withHeaders(options.headers, { "Content-Type": "application/json" }),
    body: JSON.stringify(body || {}),
    signal: options.signal
  });
  return response.json();
}

export async function apiDelete(apiKey, path, options = {}) {
  await apiRequest(apiKey, path, { method: "DELETE", signal: options.signal });
}

export async function apiDeleteJSON(apiKey, path, body, options = {}) {
  const response = await apiRequest(apiKey, path, {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body || {}),
    signal: options.signal
  });
  return response.json();
}

export async function apiGetText(apiKey, path, options = {}) {
  const response = await apiRequest(apiKey, path, { method: "GET", signal: options.signal });
  return response.text();
}

export async function apiGetBlob(apiKey, path, options = {}) {
  const response = await apiRequest(apiKey, path, { method: "GET", signal: options.signal });
  const blob = await response.blob();
  return {
    blob,
    contentType: response.headers.get("Content-Type") || blob.type || "",
    filename: parseDispositionFilename(response.headers.get("Content-Disposition"))
  };
}

export async function apiPostBlob(apiKey, path, body, options = {}) {
  const response = await apiRequest(apiKey, path, {
    method: "POST",
    headers: withHeaders(options.headers, { "Content-Type": "application/json" }),
    body: JSON.stringify(body || {}),
    signal: options.signal
  });
  const blob = await response.blob();
  return {
    blob,
    contentType: response.headers.get("Content-Type") || blob.type || "",
    filename: parseDispositionFilename(response.headers.get("Content-Disposition"))
  };
}

export async function apiPostMultipartDetailed(apiKey, path, formData, options = {}) {
  const response = await apiRequest(apiKey, path, {
    method: "POST",
    headers: withHeaders(options.headers),
    body: formData,
    signal: options.signal
  });
  return parseAPIResponse(response);
}

export function apiPostMultipart(apiKey, path, formData, options = {}) {
  return apiXHRRequest(apiKey, path, {
    method: "POST",
    body: formData,
    onProgress: options.onProgress,
    signal: options.signal
  });
}

export function apiPutMultipart(apiKey, path, formData, options = {}) {
  return apiXHRRequest(apiKey, path, {
    method: "PUT",
    body: formData,
    onProgress: options.onProgress,
    signal: options.signal
  });
}

export async function apiPostSSE(apiKey, path, body, options = {}) {
  const response = await apiRequest(apiKey, path, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "text/event-stream" },
    body: JSON.stringify(body || {}),
    signal: options.signal
  });

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const result = await reader.read();
    if (result.done) {
      break;
    }
    buffer += decoder.decode(result.value, { stream: true });
    buffer = drainSSE(buffer, options.onEvent);
  }
  buffer += decoder.decode();
  drainSSE(buffer, options.onEvent);
}

export async function streamEvents(apiKey, path, options = {}) {
  const response = await apiRequest(apiKey, path, {
    method: "GET",
    headers: { Accept: "text/event-stream" },
    signal: options.signal
  });

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const result = await reader.read();
    if (result.done) {
      break;
    }
    buffer += decoder.decode(result.value, { stream: true });
    buffer = drainSSE(buffer, options.onEvent);
  }
  buffer += decoder.decode();
  drainSSE(buffer, options.onEvent);
}

async function apiRequest(apiKey, path, options) {
  const trimmedKey = String(apiKey || "").trim();
  const sameOriginAuth = isSameOriginAuthToken(trimmedKey);
  if (!trimmedKey && !sameOriginAuth) {
    throw new Error("Enter an API key first.");
  }

  const headers = new Headers(options.headers || {});
  if (!sameOriginAuth) {
    headers.set("Authorization", `Bearer ${trimmedKey}`);
  }
  applySelectedAccountHeader(headers, path);
  const response = await fetch(path, { ...options, headers });
  if (!response.ok) {
    throw await buildError(response);
  }
  return response;
}

async function parseAPIResponse(response) {
  const contentType = response.headers.get("Content-Type") || "";
  const filename = parseDispositionFilename(response.headers.get("Content-Disposition"));
  let body = null;
  if (contentType.includes("application/json")) {
    body = await response.json();
  } else if (contentType.startsWith("text/")) {
    body = await response.text();
  } else {
    body = await response.blob();
  }
  return {
    body,
    contentType,
    filename,
    headers: response.headers
  };
}

function apiXHRRequest(apiKey, path, options = {}) {
  const trimmedKey = String(apiKey || "").trim();
  const sameOriginAuth = isSameOriginAuthToken(trimmedKey);
  if (!trimmedKey && !sameOriginAuth) {
    return Promise.reject(new Error("Enter an API key first."));
  }
  if (options.signal?.aborted) {
    return Promise.reject(createAbortError());
  }
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    let settled = false;
    const cleanup = () => {
      if (options.signal) {
        options.signal.removeEventListener("abort", handleAbort);
      }
    };
    const finishResolve = (value) => {
      if (settled) {
        return;
      }
      settled = true;
      cleanup();
      resolve(value);
    };
    const finishReject = (error) => {
      if (settled) {
        return;
      }
      settled = true;
      cleanup();
      reject(error);
    };
    const handleAbort = () => {
      request.abort();
      finishReject(createAbortError());
    };
    request.open(options.method || "POST", path, true);
    request.responseType = "text";
    if (!sameOriginAuth) {
      request.setRequestHeader("Authorization", `Bearer ${trimmedKey}`);
    }
    const accountID = selectedAccountIDForPath(path);
    if (accountID) {
      request.setRequestHeader("X-Lytebulb-Account", accountID);
    }
    request.onload = () => {
      const contentType = request.getResponseHeader("Content-Type") || "";
      if (request.status >= 200 && request.status < 300) {
        try {
          finishResolve(contentType.includes("application/json") ? JSON.parse(request.responseText || "{}") : request.responseText);
        } catch (_error) {
          finishReject(new Error("invalid server response"));
        }
        return;
      }
      finishReject(buildXHRError(request, contentType));
    };
    request.onerror = () => {
      finishReject(new Error("network request failed"));
    };
    request.onabort = () => {
      finishReject(createAbortError());
    };
    if (request.upload && typeof options.onProgress === "function") {
      request.upload.onprogress = (event) => {
        if (!event.lengthComputable) {
          options.onProgress({ loaded: event.loaded, total: 0, percent: 0 });
          return;
        }
        options.onProgress({
          loaded: event.loaded,
          total: event.total,
          percent: event.total > 0 ? Math.min(100, Math.round((event.loaded / event.total) * 100)) : 0
        });
      };
    }
    if (options.signal) {
      options.signal.addEventListener("abort", handleAbort, { once: true });
    }
    request.send(options.body || null);
  });
}

function applySelectedAccountHeader(headers, path) {
  if (headers.has("X-Lytebulb-Account")) {
    return;
  }
  const accountID = selectedAccountIDForPath(path);
  if (accountID) {
    headers.set("X-Lytebulb-Account", accountID);
  }
}

function selectedAccountIDForPath(path) {
  const accountID = loadSelectedAccountID();
  if (!accountID || pathHasAccountScope(path)) {
    return "";
  }
  return accountID;
}

function pathHasAccountScope(path) {
  try {
    const parsed = new URL(path, globalThis.window?.location?.origin || "http://localhost");
    const segments = parsed.pathname.replace(/^\/+|\/+$/g, "").split("/");
    return segments.length >= 3 && segments[0] === "v1" && segments[1] === "accounts";
  } catch (_error) {
    return false;
  }
}

async function buildError(response) {
  const contentType = response.headers.get("Content-Type") || "";
  let message = "";
  let importID = "";
  if (contentType.includes("application/json")) {
    try {
      const payload = await response.json();
      if (payload && typeof payload.error === "string") {
        message = payload.error;
      }
      importID = extractImportID(payload);
    } catch (_error) {
      message = "";
    }
  } else {
    const text = await response.text();
    message = text || `request failed with status ${response.status}`;
  }
  if (!message) {
    message = `request failed with status ${response.status}`;
  }
  const error = new Error(message);
  error.status = response.status;
  if (importID) {
    error.importID = importID;
  }
  return error;
}

function buildXHRError(request, contentType) {
  let message = request.responseText || `request failed with status ${request.status}`;
  let importID = "";
  if (contentType.includes("application/json")) {
    try {
      const payload = JSON.parse(request.responseText || "{}");
      if (payload && typeof payload.error === "string") {
        message = payload.error;
      }
      importID = extractImportID(payload);
    } catch (_error) {
      message = request.responseText || `request failed with status ${request.status}`;
    }
  }
  const error = new Error(message);
  error.status = request.status;
  if (importID) {
    error.importID = importID;
  }
  return error;
}

function extractImportID(payload) {
  if (!payload || typeof payload !== "object") {
    return "";
  }
  if (typeof payload.import_id === "string") {
    return payload.import_id;
  }
  if (payload.details && typeof payload.details === "object" && typeof payload.details.import_id === "string") {
    return payload.details.import_id;
  }
  return "";
}

function parseDispositionFilename(value) {
  const raw = String(value || "");
  const utf8Match = raw.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch (_error) {
      return utf8Match[1];
    }
  }
  const plainMatch = raw.match(/filename=\"?([^\";]+)\"?/i);
  if (plainMatch) {
    return plainMatch[1];
  }
  return "";
}

function withHeaders(...parts) {
  const headers = new Headers();
  for (const part of parts) {
    if (!part) {
      continue;
    }
    const current = new Headers(part);
    for (const [key, value] of current.entries()) {
      headers.set(key, value);
    }
  }
  return headers;
}

function createAbortError() {
  if (typeof DOMException === "function") {
    return new DOMException("The operation was aborted.", "AbortError");
  }
  const error = new Error("The operation was aborted.");
  error.name = "AbortError";
  return error;
}

function drainSSE(buffer, onEvent) {
  const blocks = buffer.split("\n\n");
  const tail = blocks.pop() || "";
  for (const block of blocks) {
    const next = parseBlock(block);
    if (next && onEvent) {
      onEvent(next);
    }
  }
  return tail;
}

function parseBlock(block) {
  const lines = block.split("\n");
  const data = [];
  let eventType = "";
  let lastID = "";
  for (const line of lines) {
    if (line.startsWith("event:")) {
      eventType = line.slice(6).trim();
    } else if (line.startsWith("id:")) {
      lastID = line.slice(3).trim();
    } else if (line.startsWith("data:")) {
      data.push(line.slice(5).trim());
    }
  }
  if (data.length === 0) {
    return null;
  }
  try {
    const payload = JSON.parse(data.join("\n"));
    return {
      id: lastID,
      eventType,
      payload
    };
  } catch (_error) {
    return null;
  }
}
